import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const DownloadCenter = ({ resources }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Downloads', count: resources?.length },
    { id: 'reading-guide', label: 'Reading Guides', count: resources?.filter(r => r?.type === 'reading-guide')?.length },
    { id: 'knowledge-area', label: 'Knowledge Area Resources', count: resources?.filter(r => r?.type === 'knowledge-area')?.length },
    { id: 'downloadable', label: 'Templates & Tools', count: resources?.filter(r => r?.type === 'downloadable')?.length },
    { id: 'interactive', label: 'Interactive Guides', count: resources?.filter(r => r?.type === 'interactive')?.length },
    { id: 'checklist', label: 'Checklists', count: resources?.filter(r => r?.type === 'checklist')?.length }
  ];

  const filteredDownloads = selectedCategory === 'all' 
    ? resources 
    : resources?.filter(r => r?.type === selectedCategory);

  const handleDownload = (resource) => {
    console.log('Downloading:', resource?.title);
  };

  const handleBulkDownload = () => {
    console.log('Downloading all resources in category:', selectedCategory);
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-2">
            Download Center
          </h2>
          <p className="text-sm text-muted-foreground">
            Access and download all study materials for offline learning
          </p>
        </div>
        <Button
          variant="default"
          iconName="Download"
          onClick={handleBulkDownload}
          className="hidden md:inline-flex"
        >
          Download All
        </Button>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 pb-6 border-b border-border">
        {categories?.map(category => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              selectedCategory === category?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
            )}
          >
            {category?.label}
            <span className="ml-2 text-xs opacity-75">({category?.count})</span>
          </button>
        ))}
      </div>

      {/* Download List */}
      <div className="space-y-3">
        {filteredDownloads?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No downloadable resources in this category</p>
          </div>
        ) : (
          filteredDownloads?.map(resource => (
            <div
              key={resource?.id}
              className="flex items-center justify-between p-4 bg-background rounded-lg border border-border hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground mb-1 truncate">
                    {resource?.title}
                  </h4>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="FileType" size={14} />
                      {resource?.format}
                    </span>
                    {resource?.fileSize && (
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={14} />
                        {resource?.fileSize}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={14} />
                      {resource?.readingTime} min
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {resource?.completionStatus === 'completed' && (
                  <div className="flex items-center gap-1 text-success text-sm">
                    <Icon name="CheckCircle2" size={16} />
                    <span className="hidden md:inline">Completed</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={() => handleDownload(resource)}
                >
                  <span className="hidden md:inline">Download</span>
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Download Statistics */}
      {filteredDownloads?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Files" size={20} className="text-primary" />
                <span className="text-sm text-muted-foreground">Total Resources</span>
              </div>
              <p className="text-2xl font-semibold text-foreground">{filteredDownloads?.length}</p>
            </div>
            <div className="bg-background p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="HardDrive" size={20} className="text-secondary" />
                <span className="text-sm text-muted-foreground">Total Size</span>
              </div>
              <p className="text-2xl font-semibold text-foreground">
                {filteredDownloads?.reduce((acc, r) => {
                  const size = parseFloat(r?.fileSize?.split(' ')?.[0]) || 0;
                  return acc + size;
                }, 0)?.toFixed(1)} MB
              </p>
            </div>
            <div className="bg-background p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle2" size={20} className="text-success" />
                <span className="text-sm text-muted-foreground">Completed</span>
              </div>
              <p className="text-2xl font-semibold text-foreground">
                {filteredDownloads?.filter(r => r?.completionStatus === 'completed')?.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadCenter;